/* Spotlight Tenant Layout — cinematic dark, theatrical, poster-headline.

   What makes this different from Editorial:
   • The masthead lives BEHIND a constant dark-canvas chrome with the
     studio name set in DM Serif Display at near-display-headline scale
     — like the title card on a movie poster. The Editorial header
     compresses on scroll; Spotlight's stays full-bleed always.
   • Underneath the studio name sits a horizontal champagne-gold rule
     and a single editorial-cap nav row centered beneath it. This is
     the marquee strip — visitor's eye lands on the studio name first,
     then the navigation, then the page content.
   • Footer reads like end credits: three centered columns, all-caps,
     hairline gold rules between, a roman-numeral year. Each block is
     a "credit" (STUDIO / CONTACT / FOLLOW).
   • A subtle radial vignette pulses outward from center on every page
     so the whole site feels like it's lit by a single overhead source.
*/

import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useTenantTheme } from "@/hooks/useTenantTheme";
import { tenantHref } from "@/lib/tenant";
import TenantSignature from "@/components/tenant/TenantSignature";
import LumetierFooterCredit from "@/components/tenant/LumetierFooterCredit";
import TenantMobileNav from "@/components/tenant/TenantMobileNav";
import TenantWordmark from "@/components/tenant/TenantWordmark";
import { Instagram, Mail } from "lucide-react";

const NAV = [
  { to: "/",          label: "Home",       end: true },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/services",  label: "Services"  },
  { to: "/contact",   label: "Contact"   },
];

function toRoman(num) {
  const map = [["M",1000],["CM",900],["D",500],["CD",400],["C",100],["XC",90],["L",50],["XL",40],["X",10],["IX",9],["V",5],["IV",4],["I",1]];
  let out = "";
  for (const [s, v] of map) {
    while (num >= v) { out += s; num -= v; }
  }
  return out;
}

export default function SpotlightTenantLayout() {
  useTenantTheme();
  const { settings } = useSiteSettings();
  const studioName = settings?.studio_name || "Studio";
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const year = new Date().getFullYear();
  const roman = toRoman(year);

  return (
    <div
      className="min-h-screen antialiased relative"
      style={{
        fontFamily: "var(--tenant-sans)",
        background: "var(--tenant-bg, #0A0908)",
        color: "var(--tenant-fg, #F5F2EC)",
      }}
      data-template="spotlight"
    >
      {/* ── Constant theatrical vignette over the whole canvas ────────── */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 z-10"
        style={{
          background:
            "radial-gradient(ellipse 90% 70% at 50% 30%, transparent 0%, color-mix(in srgb, var(--tenant-bg, #0A0908) 35%, transparent) 70%, color-mix(in srgb, var(--tenant-bg, #0A0908) 85%, transparent) 100%)",
        }}
      />

      {/* ── Marquee masthead ──────────────────────────────────────────── */}
      <header
        className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${scrolled ? "py-3 backdrop-blur-sm" : "py-6 lg:py-8"}`}
        style={{
          background: scrolled
            ? "color-mix(in srgb, var(--tenant-bg, #0A0908) 92%, transparent)"
            : "color-mix(in srgb, var(--tenant-bg, #0A0908) 60%, transparent)",
          borderBottom: scrolled ? "1px solid var(--tenant-accent)" : "1px solid transparent",
        }}
        data-testid="spotlight-tenant-nav"
      >
        {/* Mobile-only hamburger — pinned top-right of the centered
            marquee masthead so the cinematic display name stays
            symmetric. tone="dark" to keep the icon light on the dark canvas. */}
        <div className="absolute right-3 top-3 md:hidden">
          <TenantMobileNav tone="dark" ctaLabel="Book" studioName={studioName} />
        </div>
        <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
          {/* Studio wordmark — full-bleed display type at rest, compressed on scroll */}
          <Link
            to={tenantHref("/")}
            className={`block text-center transition-all duration-500 ${scrolled ? "text-lg" : "text-3xl lg:text-5xl"}`}
            style={{
              fontFamily: "'DM Serif Display', 'Playfair Display', 'Didot', serif",
              color: "var(--tenant-fg, #F5F2EC)",
              letterSpacing: scrolled ? "0.18em" : "0.04em",
              textTransform: scrolled ? "uppercase" : "none",
            }}
            data-testid="spotlight-tenant-wordmark"
          >
            <TenantWordmark
              logoUrl={settings?.logo_url}
              coverLogoUrl={settings?.cover_logo_url}
              tone="dark"
              studioName={studioName}
              imgClassName={scrolled ? "h-7 w-auto mx-auto" : "h-12 lg:h-16 w-auto mx-auto"}
            >
              {studioName}
            </TenantWordmark>
          </Link>
          {/* Hairline gold rule between studio name and nav — fades on scroll */}
          <div
            className={`mx-auto h-px transition-all duration-500 ${scrolled ? "w-0 mt-0" : "w-20 mt-3 lg:mt-4"}`}
            style={{ background: "var(--tenant-accent)" }}
          />
          {/* Nav strip — single horizontal row, hidden on tightest scroll for cleanliness */}
          <nav
            className={`mt-3 lg:mt-4 hidden md:flex justify-center gap-10 transition-opacity duration-300 ${scrolled ? "opacity-100" : "opacity-90"}`}
            aria-label="Primary"
          >
            {NAV.map((it) => (
              <NavLink
                key={it.to}
                to={tenantHref(it.to)}
                end={it.end}
                data-testid={`spotlight-tenant-nav-${it.label.toLowerCase()}`}
                className="text-[10px] uppercase tracking-[0.42em] transition-colors"
                style={({ isActive }) => ({
                  color: isActive ? "var(--tenant-accent)" : "var(--tenant-fg-soft, rgba(245,242,236,0.65))",
                })}
              >
                {it.label}
              </NavLink>
            ))}
            <Link
              to={tenantHref("/booking")}
              className="text-[10px] uppercase tracking-[0.42em] transition-colors hover:opacity-100"
              style={{ color: "var(--tenant-accent)", opacity: 0.95 }}
              data-testid="spotlight-tenant-book-cta"
            >
              Book →
            </Link>
          </nav>
        </div>
      </header>

      {/* ── Body — extra top padding so the marquee never overlaps content ── */}
      <main key={location.pathname} className="relative z-20 pt-36 lg:pt-44">
        <Outlet />
      </main>

      {/* ── End-credits footer ────────────────────────────────────────── */}
      <footer
        className="relative z-20 mt-16 pt-12 pb-10 border-t"
        style={{
          background: "var(--tenant-bg, #0A0908)",
          color: "var(--tenant-fg, #F5F2EC)",
          borderTopColor: "var(--tenant-accent)",
        }}
        data-testid="spotlight-tenant-footer"
      >
        <div className="max-w-[1480px] mx-auto px-6 lg:px-12 text-center">
          <div
            className="text-2xl lg:text-3xl mb-8"
            style={{ fontFamily: "'DM Serif Display', 'Playfair Display', serif", color: "var(--tenant-fg)" }}
          >
            {studioName}
          </div>
          <div className="mx-auto h-px w-16 mb-8" style={{ background: "var(--tenant-accent)" }} />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.42em] mb-2" style={{ color: "var(--tenant-accent)" }}>Studio</div>
              {settings?.tagline ? (
                <div className="text-sm italic" style={{ fontFamily: "var(--tenant-serif)", color: "var(--tenant-fg-soft)" }}>
                  {settings.tagline}
                </div>
              ) : (
                <div className="text-sm italic" style={{ color: "var(--tenant-fg-soft)" }}>{studioName} · {year}</div>
              )}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.42em] mb-2" style={{ color: "var(--tenant-accent)" }}>Contact</div>
              <div className="text-sm space-y-1" style={{ color: "var(--tenant-fg-soft)" }}>
                {settings?.contact_email && (
                  <a href={`mailto:${settings.contact_email}`} className="inline-flex items-center gap-2 hover:opacity-100" style={{ opacity: 0.95 }}>
                    <Mail className="w-3.5 h-3.5" strokeWidth={1.25} /> {settings.contact_email}
                  </a>
                )}
                {settings?.contact_location && (
                  <div className="italic">{settings.contact_location}</div>
                )}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.42em] mb-2" style={{ color: "var(--tenant-accent)" }}>Follow</div>
              <div className="flex justify-center gap-4" style={{ color: "var(--tenant-fg-soft)" }}>
                {settings?.instagram_url && (
                  <a href={settings.instagram_url} target="_blank" rel="noopener noreferrer" className="hover:opacity-100" style={{ opacity: 0.85 }}>
                    <Instagram className="w-4 h-4" strokeWidth={1.25} />
                  </a>
                )}
              </div>
            </div>
          </div>

          <div className="mt-10 pt-6 border-t text-[10px] uppercase tracking-[0.42em] flex flex-col sm:flex-row items-center justify-center gap-3"
               style={{ borderTopColor: "var(--tenant-fg-line)", color: "var(--tenant-fg-mute)" }}>
            <span>© {studioName}</span>
            <span className="opacity-40">·</span>
            <span style={{ color: "var(--tenant-accent)" }}>{roman}</span>
            <span className="opacity-40">·</span>
            <LumetierFooterCredit className="hover:opacity-100" testid="spotlight-lumetier-credit" />
          </div>
        </div>
      </footer>

      <TenantSignature tone="dark" />
    </div>
  );
}
